import React, { useEffect, useState } from 'react';
import { Alert, Card, Col, DatePicker, Row, Space, Statistic, Table, Typography } from 'antd';
import type { ColumnsType } from 'antd/es/table';
import moment, { Moment } from 'moment';
import { formatNu } from '../utils/formatAmount';
import {
  getProfitLoss,
  getTrialBalance,
  listGlJournals,
  GlJournal,
  ProfitLossReport,
  TrialBalanceReport,
  TrialBalanceRow,
} from '../services/glService';
import './Reports.css';
import './GlLedger.css';

const { Title, Text } = Typography;
const { RangePicker } = DatePicker;

const GlLedger: React.FC = () => {
  const [range, setRange] = useState<[Moment, Moment]>([
    moment().startOf('month'),
    moment().endOf('day'),
  ]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [tb, setTb] = useState<TrialBalanceReport | null>(null);
  const [pl, setPl] = useState<ProfitLossReport | null>(null);
  const [journals, setJournals] = useState<GlJournal[]>([]);

  const startDate = range[0].format('YYYY-MM-DD');
  const endDate = range[1].format('YYYY-MM-DD');

  useEffect(() => {
    const load = async () => {
      setLoading(true);
      setError(null);
      try {
        const [tbData, plData, journalData] = await Promise.all([
          getTrialBalance(startDate, endDate),
          getProfitLoss(startDate, endDate),
          listGlJournals(startDate, endDate),
        ]);
        setTb(tbData);
        setPl(plData);
        setJournals(journalData);
      } catch (err: any) {
        setError(err?.response?.data?.detail || 'Could not load General Ledger. Enable GL in My Profile if needed.');
        setTb(null);
        setPl(null);
        setJournals([]);
      } finally {
        setLoading(false);
      }
    };
    void load();
  }, [startDate, endDate]);

  const tbColumns: ColumnsType<TrialBalanceRow> = [
    { title: 'Code', dataIndex: 'code', width: 90 },
    { title: 'Account', dataIndex: 'name' },
    { title: 'Type', dataIndex: 'account_type', width: 100 },
    {
      title: 'Debit',
      dataIndex: 'debit',
      align: 'right',
      render: (v) => formatNu(Number(v) || 0),
    },
    {
      title: 'Credit',
      dataIndex: 'credit',
      align: 'right',
      render: (v) => formatNu(Number(v) || 0),
    },
  ];

  const journalColumns: ColumnsType<GlJournal> = [
    { title: 'Date', dataIndex: 'entry_date', width: 110 },
    { title: 'Entry #', dataIndex: 'entry_number', width: 160 },
    { title: 'Source', dataIndex: 'source_type', width: 110 },
    {
      title: 'Source ID',
      dataIndex: 'source_id',
      width: 90,
      render: (id, row) => `${row.source_type} #${id}`,
    },
    { title: 'Memo', dataIndex: 'memo', ellipsis: true },
    {
      title: 'Debit',
      dataIndex: 'total_debit',
      align: 'right',
      render: (v) => formatNu(Number(v) || 0),
    },
    {
      title: 'Credit',
      dataIndex: 'total_credit',
      align: 'right',
      render: (v) => formatNu(Number(v) || 0),
    },
  ];

  const expandedRow = (row: GlJournal) => (
    <Table
      size="small"
      pagination={false}
      rowKey="id"
      dataSource={row.lines || []}
      columns={[
        { title: 'Code', dataIndex: 'account_code', width: 80 },
        { title: 'Account', dataIndex: 'account_name' },
        {
          title: 'Debit',
          dataIndex: 'debit',
          align: 'right',
          render: (v) => formatNu(Number(v) || 0),
        },
        {
          title: 'Credit',
          dataIndex: 'credit',
          align: 'right',
          render: (v) => formatNu(Number(v) || 0),
        },
        { title: 'GST cat.', dataIndex: 'gst_category', width: 100, render: (v) => v || '—' },
        { title: 'Memo', dataIndex: 'memo', ellipsis: true },
      ]}
    />
  );

  return (
    <div className="gl-ledger-page">
      <div className="gl-ledger-head">
        <div>
          <Title level={3} style={{ margin: 0 }}>
            General Ledger
          </Title>
          <Text type="secondary">
            Auto journals from POS sales/refunds and completed GRPOs. Enable GL in My Profile first.
          </Text>
        </div>
        <Space wrap>
          <RangePicker
            value={range}
            onChange={(dates) => {
              if (dates && dates[0] && dates[1]) setRange([dates[0], dates[1]]);
            }}
            allowClear={false}
          />
        </Space>
      </div>

      {error ? <Alert type="warning" showIcon message={error} style={{ marginBottom: 16 }} /> : null}

      <Row gutter={[16, 16]}>
        <Col xs={24} sm={12} lg={6}>
          <Card className="reports-stat-card" loading={loading}>
            <Statistic title="Revenue" prefix="Nu." value={Number(pl?.revenue || 0)} precision={2} />
          </Card>
        </Col>
        <Col xs={24} sm={12} lg={6}>
          <Card className="reports-stat-card" loading={loading}>
            <Statistic title="COGS" prefix="Nu." value={Number(pl?.cogs || 0)} precision={2} />
          </Card>
        </Col>
        <Col xs={24} sm={12} lg={6}>
          <Card className="reports-stat-card" loading={loading}>
            <Statistic title="Gross profit" prefix="Nu." value={Number(pl?.gross_profit || 0)} precision={2} />
          </Card>
        </Col>
        <Col xs={24} sm={12} lg={6}>
          <Card className="reports-stat-card" loading={loading}>
            <Statistic title="Net profit" prefix="Nu." value={Number(pl?.net_profit || 0)} precision={2} />
          </Card>
        </Col>
      </Row>

      <Card title="Trial balance" style={{ marginTop: 16 }} loading={loading}>
        <Table
          size="small"
          rowKey="account_id"
          columns={tbColumns}
          dataSource={tb?.rows || []}
          pagination={false}
          scroll={{ x: 700 }}
          summary={() => (
            <Table.Summary.Row>
              <Table.Summary.Cell index={0} colSpan={3}>
                <Text strong>Totals</Text>
              </Table.Summary.Cell>
              <Table.Summary.Cell index={3} align="right">
                <Text strong>{formatNu(Number(tb?.total_debit || 0))}</Text>
              </Table.Summary.Cell>
              <Table.Summary.Cell index={4} align="right">
                <Text strong>{formatNu(Number(tb?.total_credit || 0))}</Text>
              </Table.Summary.Cell>
            </Table.Summary.Row>
          )}
        />
      </Card>

      <Card title="Journals" style={{ marginTop: 16 }} loading={loading}>
        <Table
          size="small"
          rowKey="id"
          columns={journalColumns}
          dataSource={journals}
          pagination={{ pageSize: 20 }}
          expandable={{ expandedRowRender: expandedRow }}
          scroll={{ x: 900 }}
        />
      </Card>
    </div>
  );
};

export default GlLedger;
